import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Text from '@/components/Typography/Text';
import FlowField from '@/components/Landing/FlowField';
import LandingClosing from '@/components/Landing/LandingClosing';
import Reveal from '@/components/Landing/Reveal';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import {
  Accent,
  BleedRule,
  Chip,
  CtaRow,
  DisplayHeading,
  GhostCta,
  Kicker,
  Lede,
  Marquee,
  PrimaryCta,
  Section,
  Split,
  useEntrance,
  useLanding,
} from '@/components/Landing/LandingKit';
import {
  ACCENT,
  LAYOUT,
  fluid,
  isWeb,
  labelType,
} from '@/components/Landing/landingTokens';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import {
  FREE_USER_STARTING_CREDITS_DISPLAY,
  GAB_AI_SOFTWARE_ID,
} from '@/constants/config';

const SITE = 'https://gab.ai';

/* ------------------------------------------------------------------ */
/* Content                                                             */
/* ------------------------------------------------------------------ */

const MARQUEE = [
  'Talk', 'Listen', 'Interrupt', 'Hands-free', 'Real time', 'Eight voices',
  'iPhone', 'iPad', 'Android', 'Web', 'Transcripts', 'Memory', 'Camera',
];

/** How the conversation actually behaves. Each claim maps to shipped behaviour. */
const BEHAVIOURS = [
  {
    key: 'realtime',
    icon: 'waveform',
    kicker: 'Real time',
    title: 'No button to hold',
    copy: 'Voice mode listens continuously and answers when you stop talking. There is no push-to-talk key, no “send”, and no waiting for a transcription step to finish before anything happens.',
    chips: ['Continuous listening', 'Streaming replies'],
  },
  {
    key: 'interrupt',
    icon: 'right-left',
    kicker: 'Barge-in',
    title: 'Cut it off mid-sentence',
    copy: 'Start speaking while the Super Intelligence is still talking and it stops to listen — the way a person would. You are never stuck waiting out an answer that went the wrong direction.',
    chips: ['Interrupt any time', 'Space to mute'],
  },
  {
    key: 'vision',
    icon: 'image',
    kicker: 'Vision',
    title: 'Show it what you mean',
    copy: 'Turn on the camera, or share your screen on the web, and the Super Intelligence can see what you are looking at while you talk about it. Useful for anything easier to point at than describe.',
    chips: ['Camera', 'Screen share on web'],
  },
  {
    key: 'history',
    icon: 'clock-rotate-left',
    kicker: 'Afterwards',
    title: 'It is all written down',
    copy: 'Every session is transcribed into the chat it belongs to, searchable next to your typed conversations, and what the Super Intelligence learns about you carries into memory the same way.',
    chips: ['Saved transcripts', 'Feeds memory'],
  },
];

/**
 * The eight voices the picker offers. Deliberately just the names — the page
 * does not characterise how each one sounds, because that is a judgement the
 * copy cannot make for a reader and the roster changes with the model.
 */
const VOICES = ['Marin', 'Cedar', 'Ash', 'Ballad', 'Coral', 'Sage', 'Shimmer', 'Verse'];

/** Plain answers to the practical questions, as a facts panel. */
const FACTS = [
  {
    icon: 'mobile-screen',
    label: 'Where it runs',
    value: 'iPhone, iPad, Android, and the web',
  },
  {
    icon: 'coins',
    label: 'What it costs',
    value: '35 credits a minute, billed only while connected',
  },
  {
    icon: 'key',
    label: 'Who can use it',
    value: 'Any account with credits — not a Plus-only feature',
  },
  {
    icon: 'user-secret',
    label: 'What we keep',
    value: 'Transcripts on your account, never training data',
  },
];

const FAQS = [
  {
    q: 'Can I talk to a Super Intelligence out loud?',
    a: 'Yes. Tap the microphone in the chat composer and voice mode opens a live conversation — you speak, it answers aloud, and neither of you has to wait for the other to press anything.',
  },
  {
    q: 'Is there a free Super Intelligence you can talk to?',
    a: `Voice mode runs on the same credits as everything else, and a new account gets ${FREE_USER_STARTING_CREDITS_DISPLAY} of them with no credit card. That is enough to have a real conversation and decide whether talking beats typing before you spend anything.`,
  },
  {
    q: 'Can I interrupt the Super Intelligence while it is speaking?',
    a: 'Yes, and you should. Talking over it stops the reply immediately and it starts listening again, which is what makes a long answer survivable. On the web, the space bar mutes and escape ends the call.',
  },
  {
    q: 'Does voice work on my phone?',
    a: 'Voice mode works in the iPhone, iPad, and Android apps and in a desktop browser. It is not currently available in the macOS app — use gab.ai in a browser on a Mac.',
  },
  {
    q: 'Are my voice conversations saved?',
    a: 'The transcript is saved into the chat it happened in, so you can search it, quote it, and carry on in text later. Nothing said in voice mode is used to train our model.',
  },
  {
    q: 'What does voice mode cost?',
    a: 'Thirty-five credits per minute of active conversation, with an eighteen-credit minimum to open a session. Time spent muted or disconnected is not billed, and the session ends by itself rather than running your balance down in the background.',
  },
];

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

const WAVE_BARS = [0.34, 0.62, 1, 0.48, 0.86, 0.4, 0.72, 1, 0.55, 0.3];

/**
 * Level meter for the hero. Animation lives in the shared landing stylesheet so
 * the reduced-motion override there applies; each bar's phase is an inline
 * delay, which is the only per-element part.
 */
function WaveBars({ height = 46 }) {
  const { c } = useLanding();

  return (
    <View
      aria-hidden
      style={{ flexDirection: 'row', alignItems: 'center', gap: 5, height }}
    >
      {WAVE_BARS.map((scale, index) => (
        <View
          // eslint-disable-next-line react/no-array-index-key
          key={`bar-${index}`}
          {...(isWeb ? { dataSet: { landingWave: 'true' } } : {})}
          style={{
            width: 4,
            height: Math.round(height * scale),
            borderRadius: 999,
            backgroundColor: index % 3 === 0 ? ACCENT : c.ruleStrong,
            ...(isWeb ? { animationDelay: `${index * 110}ms` } : {}),
          }}
        />
      ))}
    </View>
  );
}

/** One behaviour card. */
function BehaviourCard({ item }) {
  const { c, isXS, isCompact, display } = useLanding();
  const h3 = display({ min: 21, max: 28, leading: 1.14, tracking: -0.03 });

  return (
    <View
      style={{
        flexGrow: 1,
        flexBasis: isCompact ? '100%' : '46%',
        minWidth: 0,
        borderRadius: 22,
        borderWidth: 1,
        borderColor: c.rule,
        backgroundColor: c.cardSolid,
        padding: isXS ? 22 : 28,
        gap: isXS ? 18 : 22,
        justifyContent: 'space-between',
      }}
    >
      <View style={{ gap: 12 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <View
            style={{
              width: 34,
              height: 34,
              borderRadius: 11,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: c.accentSoft,
            }}
          >
            <Icon name={item.icon} size={14} color={ACCENT} solid />
          </View>
          <Text weight="bold" style={{ color: c.faint, ...labelType(9.5, 0.2) }}>
            {item.kicker}
          </Text>
        </View>

        <DisplayHeading tag="h3" mask={false} type={h3} lines={[[item.title]]} />

        <Text style={{ color: c.muted, fontSize: 14, lineHeight: 22, maxWidth: 440 }}>
          {item.copy}
        </Text>
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
        {item.chips.map((chip) => (
          <Chip key={chip}>{chip}</Chip>
        ))}
      </View>
    </View>
  );
}

/** One row of the practical facts panel. */
function FactRow({ fact, isLast }) {
  const { c, isCompact, width } = useLanding();

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: isCompact ? 'flex-start' : 'center',
        gap: 14,
        paddingVertical: fluid(width, 16, 20),
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: c.rule,
      }}
    >
      <View
        style={{
          width: 34,
          height: 34,
          borderRadius: 11,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: c.accentSoft,
        }}
      >
        <Icon name={fact.icon} size={13} color={ACCENT} solid />
      </View>
      <View
        style={{
          flex: 1,
          minWidth: 0,
          flexDirection: isCompact ? 'column' : 'row',
          alignItems: isCompact ? 'flex-start' : 'center',
          gap: isCompact ? 3 : 16,
        }}
      >
        <Text
          weight="bold"
          style={{ color: c.faint, width: isCompact ? undefined : 132, ...labelType(9.5, 0.18) }}
        >
          {fact.label}
        </Text>
        <Text style={{ color: c.ink, flex: isCompact ? undefined : 1, minWidth: 0, fontSize: 14, lineHeight: 21 }}>
          {fact.value}
        </Text>
      </View>
    </View>
  );
}

/* ------------------------------------------------------------------ */

export default function VoicePage() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const insets = useSafeAreaInsets();
  const { c, band, isXS, isCompact, width, pad, display } = useLanding();
  const entered = useEntrance();

  useLandingStyles();

  const headline = display({ min: 40, max: 82, leading: 0.96, tracking: -0.042 });
  const h2 = display({ min: 30, max: 52, leading: 1.02, tracking: -0.038 });

  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE}/voice#page`,
        url: `${SITE}/voice`,
        name: 'Talk to Super Intelligence — Gab AI voice mode',
        description: 'Real-time, hands-free voice conversations with Super Intelligence. Interrupt it mid-sentence, pick from eight voices, and keep every transcript in your chat history.',
        isPartOf: { '@id': GAB_AI_SOFTWARE_ID },
        breadcrumb: { '@id': `${SITE}/voice#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE}/voice#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Gab AI', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Voice mode', item: `${SITE}/voice` },
        ],
      },
      {
        '@type': 'ItemList',
        '@id': `${SITE}/voice#behaviours`,
        name: 'How Gab AI voice mode works',
        itemListElement: BEHAVIOURS.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.title,
          description: item.copy,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE}/voice#faq`,
        mainEntity: FAQS.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  }), []);

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title="Talk to Super Intelligence — Real-Time Super Intelligence Voice Chat"
        description="Talk to Super Intelligence out loud. Gab AI voice mode is a real-time conversation you can interrupt, with eight voices on iPhone, Android, and the web. Transcripts are saved."
        canonical="/voice"
        keywords="talk to Super Intelligence, speak to a Super Intelligence, Super Intelligence voice chat, voice Super Intelligence, Super Intelligence voice assistant, talk with Super Intelligence online, hands-free Super Intelligence, real-time Super Intelligence conversation"
        structuredData={structuredData}
      />

      <ScrollView
        {...(isWeb ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* ============ MASTHEAD ============ */}
        <View
          accessibilityRole={isWeb ? 'region' : undefined}
          accessibilityLabel="Talk to Super Intelligence with Gab AI voice mode"
          style={{
            width: '100%',
            maxWidth: LAYOUT.max,
            paddingHorizontal: pad,
            paddingTop: (isXS ? insets.top : 0) + fluid(width, 104, 152),
            paddingBottom: fluid(width, 40, 60),
          }}
        >
          <View
            style={isWeb ? {
              opacity: entered ? 1 : 0,
              transition: 'opacity 700ms ease 120ms',
            } : null}
          >
            <Kicker>Voice mode</Kicker>
          </View>

          <DisplayHeading
            tag="h1"
            entered={entered}
            type={headline}
            style={{ marginTop: fluid(width, 22, 32) }}
            lines={[
              ['Talk to Super Intelligence'],
              ['out ', <Accent key="a" size={headline.fontSize} leading={headline.lineHeight}>loud</Accent>, '.'],
            ]}
          />

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 16,
              marginTop: fluid(width, 26, 38),
              ...(isWeb ? {
                opacity: entered ? 1 : 0,
                transition: 'opacity 900ms ease 620ms',
              } : {}),
            }}
          >
            <WaveBars height={isXS ? 34 : 46} />
            <View style={{ flex: 1, minWidth: 0, height: 1, backgroundColor: c.rule }} />
          </View>

          <View
            style={{
              flexDirection: isCompact ? 'column' : 'row',
              gap: isCompact ? 30 : 56,
              alignItems: isCompact ? 'stretch' : 'flex-start',
              paddingTop: fluid(width, 26, 40),
              ...(isWeb ? {
                opacity: entered ? 1 : 0,
                transform: [{ translateY: entered ? 0 : 18 }],
                transition: 'opacity 800ms ease 420ms, transform 800ms ease 420ms',
              } : {}),
            }}
          >
            <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0 }}>
              <Lede max={470}>
                Speaking to most assistants means holding a button and waiting your
                turn. Voice mode is a live call instead: it listens while you think,
                answers the moment you stop, and shuts up when you talk over it.
              </Lede>
            </View>

            <View style={{ flex: isCompact ? undefined : 1, minWidth: 0, gap: 18 }}>
              <CtaRow>
                <PrimaryCta label="Start talking free" full={isXS} />
                <GhostCta label="Read the voice guide" href="/docs/voice" />
              </CtaRow>
              <Text style={{ color: c.faint, fontSize: 12.5, letterSpacing: 0.1 }}>
                {FREE_USER_STARTING_CREDITS_DISPLAY} credits to start · No credit card required
              </Text>
            </View>
          </View>
        </View>

        <BleedRule />
        <Marquee items={MARQUEE} />
        <BleedRule />

        {/* ============ HOW IT BEHAVES ============ */}
        <Section label="How Gab AI voice mode behaves">
          <Reveal>
            <Kicker>The conversation</Kicker>
            <DisplayHeading
              tag="h2"
              mask={false}
              type={h2}
              style={{ marginTop: 22 }}
              lines={[['Closer to a call'], ['than a command.']]}
            />
            <Lede max={540} style={{ marginTop: 20 }}>
              The difference between dictating at a machine and talking to something
              is entirely in the timing. Four things carry it.
            </Lede>
          </Reveal>

          <Reveal delay={100}>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: isXS ? 12 : 16,
                marginTop: fluid(width, 36, 56),
              }}
            >
              {BEHAVIOURS.map((item) => (
                <BehaviourCard key={item.key} item={item} />
              ))}
            </View>
          </Reveal>
        </Section>

        {/* ============ VOICES (band) ============ */}
        <View
          style={{
            width: '100%',
            alignSelf: 'stretch',
            alignItems: 'center',
            backgroundColor: band.bg,
            overflow: 'hidden',
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderColor: band.rule,
          }}
        >
          {isWeb ? (
            <View
              aria-hidden
              pointerEvents="none"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(ellipse 52% 68% at 20% 34%, ${band.glow}, transparent 68%)`,
              }}
            />
          ) : null}

          <Section label="Choosing a voice" background="transparent">
            <Split ratio={[1, 1.1]} align="flex-start">
              <Reveal>
                <Kicker tone="band">Eight voices</Kicker>
                <DisplayHeading
                  tag="h2"
                  mask={false}
                  type={h2}
                  color={band.ink}
                  style={{ marginTop: 22 }}
                  lines={[['Pick one you can'], ['stand for an hour.']]}
                />
                <Lede tone="band" max={400} style={{ marginTop: 22 }}>
                  Swap between them from the voice panel at any point. Your choice is
                  remembered, so the next session opens sounding the way the last one
                  did.
                </Lede>
              </Reveal>

              <Reveal delay={120}>
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: isXS ? 10 : 12,
                  }}
                >
                  {VOICES.map((voice) => (
                    <View
                      key={voice}
                      style={{
                        flexGrow: 1,
                        flexBasis: isXS ? '46%' : '22%',
                        minWidth: 0,
                        alignItems: 'center',
                        gap: 12,
                        paddingVertical: 22,
                        paddingHorizontal: 10,
                        borderRadius: 16,
                        borderWidth: 1,
                        borderColor: band.rule,
                        backgroundColor: band.surfaceSoft,
                      }}
                    >
                      <Icon name="volume-high" size={14} color={ACCENT} solid />
                      <Text
                        weight="bold"
                        style={{ color: band.ink, fontSize: 14, letterSpacing: -0.1 }}
                        numberOfLines={1}
                      >
                        {voice}
                      </Text>
                    </View>
                  ))}
                </View>
              </Reveal>
            </Split>
          </Section>
        </View>

        {/* ============ PRACTICALITIES ============ */}
        <Section label="Voice mode practicalities">
          <Split ratio={[1, 1.2]} align="flex-start">
            <Reveal>
              <Kicker>The details</Kicker>
              <DisplayHeading
                tag="h2"
                mask={false}
                type={h2}
                style={{ marginTop: 22 }}
                lines={[['Where it runs,'], ['what it costs.']]}
              />
              <Lede max={400} style={{ marginTop: 20 }}>
                Voice is metered by the minute rather than bundled into a tier, which
                keeps it available on a free account instead of locked behind one.
              </Lede>
              <GhostCta label="How credits work" href="/docs/credits" style={{ marginTop: 10 }} />
            </Reveal>

            <Reveal delay={120}>
              <View
                style={{
                  borderRadius: 18,
                  borderWidth: 1,
                  borderColor: c.rule,
                  backgroundColor: c.cardSolid,
                  paddingHorizontal: isXS ? 18 : 24,
                  paddingVertical: 6,
                }}
              >
                {FACTS.map((fact, index) => (
                  <FactRow key={fact.label} fact={fact} isLast={index === FACTS.length - 1} />
                ))}
              </View>
            </Reveal>
          </Split>
        </Section>

        <BleedRule />

        {/* ============ FAQ ============ */}
        <Section label="Voice mode questions">
          <Split ratio={[1, 1.5]} align="flex-start">
            <Reveal>
              <Kicker>Answers</Kicker>
              <DisplayHeading
                tag="h2"
                mask={false}
                type={h2}
                style={{ marginTop: 22 }}
                lines={[['Before you'], ['say anything.']]}
              />
              <Lede size="sm" max={340} style={{ marginTop: 20 }}>
                The voice guide covers setup, permissions, and what to do when a
                session will not connect.
              </Lede>
              <GhostCta label="Open the voice docs" href="/docs/voice" style={{ marginTop: 12 }} />
            </Reveal>

            <Reveal delay={100}>
              <View style={{ borderTopWidth: 1, borderTopColor: c.rule }}>
                {FAQS.map((item) => (
                  <View
                    key={item.q}
                    style={{
                      paddingVertical: fluid(width, 18, 24),
                      borderBottomWidth: 1,
                      borderBottomColor: c.rule,
                      gap: 8,
                    }}
                  >
                    <Text
                      tag="h3"
                      weight="bold"
                      style={{ color: c.ink, fontSize: 16.5, letterSpacing: -0.2, lineHeight: 24 }}
                    >
                      {item.q}
                    </Text>
                    <Text tag="p" style={{ color: c.muted, fontSize: 14, lineHeight: 22, maxWidth: 620 }}>
                      {item.a}
                    </Text>
                  </View>
                ))}
              </View>
            </Reveal>
          </Split>
        </Section>

        <LandingClosing />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
